import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _set from "lodash/set";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.number.constructor.js";
import { CATEGORY_NAMES, defaultCategorySpend } from '@nerdwallet/cc-spending-defaults';
/**
 * All of these fields are currently required in a query to the offer API
 */

export var requiredSpendCategories = [CATEGORY_NAMES.GAS, CATEGORY_NAMES.GROCERY, CATEGORY_NAMES.DINING, CATEGORY_NAMES.TRAVEL, CATEGORY_NAMES.OTHER];
/**
 * Creates a root node for a complex query for the offer API payload. A complex query enables nested
 * conditionals, such as "I want a card with this id AND (has the hotel category OR the airline
 * category)." The node acts as the parentheses in the prior statement.
 *
 * Note: If there is no complex query in your query list, all of the conditions are implicitly ANDed
 * together
 *
 * @param operator {String} - 'and' or 'or'
 * @param params {Array} - The subjects of the operator
 */

export var cqNode = function cqNode(operator, params) {
  return {
    type: operator,
    value: params
  };
};
/**
 * Creates a leaf node for a complex query for the offer API payload. This creates a condition
 * identical to adding a query parameter to the payload, but unlike using .addQueryParam(), can be
 * inserted into a complex query conditional.
 *
 * @param field {String} - 'and' or 'or'
 * @param type {String} - The operator to be performed when checking the field, e.g. 'eq'
 * @param value {String} - The value to perform the operation against
 */

export var cqLeaf = function cqLeaf(field, type, value) {
  return {
    field: field,
    type: type,
    value: value
  };
};
/**
 * Creates an offer payload object. This is an OOP-styled object with getters and setters for the
 * various fields. To access the actual JSON payload, use payload.getJSON().
 *
 * The general structure of the query object is:
 * {
 *   parameters: {
 *     // metadata used for calculating derived details
 *     // e.g. total_debt, used for determining optimal savings for users seeking a balance transfer
 *     // card
 *   },
 *   query: [
 *     // ifs, ands, and conditionals to limit the result set
 *     // e.g.: 'offer.details.legacy_id' 'eq' 4017
 *   ],
 *   sort: [
 *     // a prioritized list of conditions to sort the results on
 *     // e.g. 'offer.derived_details.net_rewards' 'desc'
 *   ],
 *   pagination: {
 *     // determines how many results to return and allows pagination
 *     // e.g. offset: 0, limit: 3,
 *   }
 * }
 */

export var createOfferPayload = function createOfferPayload() {
  return function () {
    var payload = {
      parameters: {
        category_spend: _objectSpread({}, defaultCategorySpend),
        debt_payment: 0,
        // set to total_debt / number_of_months for now
        fico_score: 750,
        number_of_months: 24,
        // default to 24
        total_debt: 0 // only send this if balance transfer or future purchase

      },
      query: [],
      sort: [],
      pagination: {
        offset: 0,
        limit: 3
      },
      fields: []
    };

    var setCategorySpend = function setCategorySpend(category, spend) {
      _set(payload, ['parameters', 'category_spend', category], spend);
    };

    var getAllCategorySpend = function getAllCategorySpend() {
      return _get(payload, ['parameters', 'category_spend']);
    };
    /**
     * Set spend for each category indicated in the categorySpend object.
     * @param {Object} categorySpend - an object mapping category-name (string)
     *                                 to category-spend (number)
     */


    var setAllCategorySpend = function setAllCategorySpend(categorySpend) {
      if (!categorySpend || _isEmpty(categorySpend)) {
        return;
      }

      var newCategorySpend = categorySpend;
      requiredSpendCategories.forEach(function (categoryName) {
        if (!newCategorySpend[categoryName]) {
          newCategorySpend[categoryName] = 0;
        }
      });

      _set(payload, ['parameters', 'category_spend'], newCategorySpend);
    };

    var setExistingCardAPR = function setExistingCardAPR(apr) {
      // apr is the percentage form: e.g. 18% = send the integer 18
      _set(payload, ['parameters', 'existing_card_apr'], apr);
    };

    var setDebtPayment = function setDebtPayment(payment) {
      _set(payload, ['parameters', 'debt_payment'], payment);
    };

    var setFicoScore = function setFicoScore(score) {
      _set(payload, ['parameters', 'fico_score'], score);
    };

    var getFicoScore = function getFicoScore() {
      return _get(payload, ['parameters', 'fico_score']);
    };

    var setNumberOfMonths = function setNumberOfMonths(months) {
      _set(payload, ['parameters', 'number_of_months'], months);
    };

    var getNumberOfMonths = function getNumberOfMonths() {
      return _get(payload, ['parameters', 'number_of_months']);
    };

    var setTotalDebt = function setTotalDebt(debt) {
      _set(payload, ['parameters', 'total_debt'], debt);
    };

    var getTotalDebt = function getTotalDebt() {
      return _get(payload, ['parameters', 'total_debt']);
    };

    var addQueryParam = function addQueryParam(field, type, value) {
      payload.query.push({
        field: field,
        type: type,
        value: value
      });
    };

    var addComplexQuery = function addComplexQuery(complexQuery) {
      payload.query.push(complexQuery);
    };

    var addSortField = function addSortField(field, order) {
      payload.sort.push({
        field: field,
        order: order
      });
    };

    var addFields = function addFields(fields) {
      payload.fields = fields;
    };

    var setPagination = function setPagination(offset, limit) {
      payload.pagination.offset = offset;
      payload.pagination.limit = limit;
    };

    var getPagination = function getPagination() {
      return payload.pagination;
    };

    var getJSON = function getJSON() {
      return payload;
    };

    var addRootComplexQuery = function addRootComplexQuery(operator, params) {
      var origQuery = payload.query.map(function (query) {
        return cqLeaf(query.field, query.type, query.value);
      });
      var rootQuery = cqNode(operator, [].concat(_toConsumableArray(params), [cqNode('and', origQuery)]));
      payload.query = [];
      payload.query.push(rootQuery);
    };

    return {
      addComplexQuery: addComplexQuery,
      addFields: addFields,
      addQueryParam: addQueryParam,
      addRootComplexQuery: addRootComplexQuery,
      addSortField: addSortField,
      getAllCategorySpend: getAllCategorySpend,
      getFicoScore: getFicoScore,
      getJSON: getJSON,
      getNumberOfMonths: getNumberOfMonths,
      getPagination: getPagination,
      getTotalDebt: getTotalDebt,
      setAllCategorySpend: setAllCategorySpend,
      setCategorySpend: setCategorySpend,
      setDebtPayment: setDebtPayment,
      setExistingCardAPR: setExistingCardAPR,
      setFicoScore: setFicoScore,
      setNumberOfMonths: setNumberOfMonths,
      setPagination: setPagination,
      setTotalDebt: setTotalDebt
    };
  }();
};
/**
 * Creates a payload with the basic necessities for querying for a single card by slug
 *
 * @param cardSlug {String} - the slug of the card you are looking for
 * @returns {Object} an offer API payload
 */

export var getPayloadForSingleCardWithSlug = function getPayloadForSingleCardWithSlug(cardSlug) {
  var payload = createOfferPayload();
  payload.addQueryParam('offer.details.slug', 'eq', cardSlug);
  payload.setPagination(0, 1);
  return payload;
};
/**
 * Creates a payload with the basic necessities for querying for a single card by legacy id
 *
 * @param legacyID {Number} - the legacy id of the card you are looking for
 * @returns {Object} an offer API payload
 */

export var getPayloadForSingleCardWithLegacyID = function getPayloadForSingleCardWithLegacyID(legacyID) {
  var payload = createOfferPayload();
  payload.addQueryParam('offer.details.legacy_id', 'eq', +legacyID);
  payload.setPagination(0, 1);
  return payload;
};
/**
 * Creates a payload with the basic necessities for querying by legacy id
 *
 * @param legacyIds {Array} - the legacy IDs of the cards you are looking for
 * @returns {Object} an offer API payload
 */

export var getPayloadForCardsByLegacyId = function getPayloadForCardsByLegacyId(legacyIds) {
  if (legacyIds.length === 1) {
    return getPayloadForSingleCardWithLegacyID(legacyIds[0]);
  }

  var legacyIdNums = legacyIds.map(Number);
  var payload = createOfferPayload();
  payload.addQueryParam('offer.details.legacy_id', 'in', legacyIdNums);
  payload.setPagination(0, legacyIdNums.length);
  return payload;
};
/**
 * Creates a payload for getting all credit cards (inactive/active) by institution name
 *
 * @param name {String} - the name of the institution to query for
 * @returns {Object} an offer API payload
 */

export var getPayloadForAllCardsByInstitution = function getPayloadForAllCardsByInstitution(name) {
  var payload = createOfferPayload();
  payload.addQueryParam('offer.product.institution.name', 'eq', name);
  payload.setPagination(0, 100);
  return payload;
};
/**
 * Creates a payload for getting content tips and related content for a specific card
 *
 * @param legacyId {Number} - the legacy id of the card you are looking for
 * @returns {Object} an offer API payload
 */

export var getTipsForSingleCardByLegacyId = function getTipsForSingleCardByLegacyId(legacyId) {
  var payload = createOfferPayload();
  payload.addQueryParam('offer.details.legacy_id', 'eq', legacyId);
  payload.addFields(['offer.details.legacy_id', 'offer.details.content_tips', 'offer.details.related_content']);
  return payload;
};